import { useCallback, useEffect, useState } from 'react';
import Typography from '@mui/material/Typography';
import { pointsCentroid } from '../util/atom_distance_utils';
import useSelectedAtoms from './common/helpers/useSelectedAtoms';
import useEventBrokerSubscription from './common/helpers/useEventBrokerSubscription';

// Any of these on the page means the user has moved on, so a notice is stale.
const NOTICE_CLEARING_INPUTS = ['pointerdown', 'wheel', 'keydown'];
const NOTICE_CLEARING_EVENTS = ['selectionDisplay', 'zapAll'];

/**
 * Component for a status bar at the bottom of the screen.
 */
export default function StatusBar() {
    // Calculate and store desired information about selected atoms
    const selectedAtoms = useSelectedAtoms();
    const selectedAtomInfo = extractSelectedAtomInfo(selectedAtoms);
    const { count: selectedAtomCount, centroid } = selectedAtomInfo;
    const nAtomsStr = selectedAtomCount.toLocaleString();
    const selectedMsg = selectedAtomCount > 0
        ? `${nAtomsStr} atom${selectedAtomCount === 1 ? '' : 's'} selected`
        : '';

    // So far only the selected atom count is displayed.
    // It might be nice to one day include atom coordinates, distances, bond angles, etc.
    let msg = selectedMsg;

    if (selectedAtomCount > 1) {
        msg += `. Centroid: ${centroid.map((n) => n.toFixed(2))}`;
    }

    const notice = useTransientNotice();

    const style = {
        position: 'absolute',
        bottom: 0,
        right: 0,
        marginBottom: 'var(--mol-button-group-border)',
        marginRight: 'var(--mol-button-group-border)',
    };

    return (
        <Typography id="status-bar" as="div" style={style}>{msg || notice}</Typography>
    );
}

/**
 * A one-off message (eg "Nothing selected" from the select box), unlike the selection
 * count, which persists as long as the selection does. It lasts until the user's next
 * mouse, wheel or key input anywhere on the page, or until the selection changes.
 * The listeners are added after the notice renders, so the click or Enter that
 * submitted the query cannot clear it.
 */
function useTransientNotice() {
    const [notice, setNotice] = useState('');
    const clear = useCallback(() => setNotice(''), []);
    const show = useCallback((evt, text) => setNotice(text), []);

    useEventBrokerSubscription('selectionNotice', show);
    useEventBrokerSubscription(NOTICE_CLEARING_EVENTS, clear);

    useEffect(() => {
        if (!notice) return undefined;
        // Capture phase, so the 3D canvas or a dialog stopping propagation can't keep it up.
        for (const input of NOTICE_CLEARING_INPUTS) {
            window.addEventListener(input, clear, { capture: true, passive: true });
        }
        return () => {
            for (const input of NOTICE_CLEARING_INPUTS) {
                window.removeEventListener(input, clear, { capture: true });
            }
        };
    }, [notice, clear]);

    return notice;
}

function extractSelectedAtomInfo(selectedAtoms) {
    return {
        count: selectedAtoms.length,
        centroid: pointsCentroid(selectedAtoms),
    };
}
